// this is my current courses page code

import React, { useState, useEffect, useRef } from "react";
import Layout from "../Layout";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import { Button, message, Typography } from "antd";
import {
    LeftOutlined,
    RightOutlined,
} from "@ant-design/icons";

import { motion, AnimatePresence } from "framer-motion";
import {
    BookOpen,
    ChevronLeft,
    ChevronRight,
    Pause,
    FileText,
    PlusCircle,
    CheckCircle,
    ExternalLink,
    BookMarked,
    Clock,
    ArrowLeft,
    Volume2,
    Loader,
    Award,
    Target
} from "lucide-react";
import { API_CONFIG } from "../config/api";

const { Title } = Typography;
// Animation variants
const containerVariants = {
    hidden: {},
    show: {
        transition: {
            staggerChildren: 0.12,
            delayChildren: 0.1
        }
    }
};

const getCardVariants = (idx, total) => {
    const fromX = idx < total / 2 ? -80 : 80;
    return {
        hidden: { opacity: 0, x: fromX, scale: 0.85, rotate: idx % 2 === 0 ? -5 : 5 },
        show: {
            opacity: 1,
            x: 0,
            scale: 1,
            rotate: 0,
            transition: {
                type: "spring",
                duration: 0.7,
                bounce: 0.35
            }
        }
    };
};
const CoursesPage = () => {
    const [books, setBooks] = useState([]);
    const [selectedBook, setSelectedBook] = useState(null);
    const [topics, setTopics] = useState([]);
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [topicContent, setTopicContent] = useState("");
    const [audioUrl, setAudioUrl] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [scrollPercentage, setScrollPercentage] = useState(0);
    const [loadingBooks, setLoadingBooks] = useState(false);
    const [materialsModal, setMaterialsModal] = useState({ open: false, materials: [] });
    const [loadingTopics, setLoadingTopics] = useState(false);
    const audioRef = useRef(null);
    const contentRef = useRef(null);
    const server_url = API_CONFIG.BASE_URL;

    useEffect(() => {
        const fetchBooks = async () => {
            setLoadingBooks(true);
            const accessToken = localStorage.getItem("accessToken");
            try {
                const response = await fetch(`${server_url}/books`, {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                });
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                setBooks(data.books);
                setLoadingBooks(false);
            } catch (error) {
                console.error("Error fetching books:", error);
                setLoadingBooks(false);
                message.error("Failed to fetch books.");
            }
        };
        fetchBooks();
    }, [server_url]);

    const fetchTopics = async (bookId) => {
        const accessToken = localStorage.getItem("accessToken");
        setLoadingTopics(true);
        try {
            const response = await fetch(`${server_url}/books/${bookId}/topics`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            setTopics(data.topics);
            if (data.topics.length > 0) {
                fetchTopicContent(data.topics[0].topic_id);
                setSelectedTopic(data.topics[0]);
            }
            setLoadingTopics(false);
        } catch (error) {
            console.error("Error fetching topics:", error);
            setLoadingTopics(false);
            message.error("Failed to fetch topics.");
        }
    };

    const fetchTopicContent = async (topicId) => {
        const accessToken = localStorage.getItem("accessToken");
        try {
            const response = await fetch(`${server_url}/content/${topicId}`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            setTopicContent(data.content);
            if (contentRef.current) {
                contentRef.current.scrollTop = 0; // Reset scroll to the top for the new topic
            }
        } catch (error) {
            console.error("Error fetching topic content:", error);
            message.error("Failed to fetch topic content.");
        }
    };

    const sendScrollPercentage = async (topicId, percentage) => {
        console.log(topicId);
        const accessToken = localStorage.getItem("accessToken");
        try {
            await fetch(`${server_url}/lectures/watch/${topicId}/${percentage}`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": "application/json",
                },
            });
        } catch (error) {
            console.error("Error sending scroll percentage:", error);
        }
    };

    const enrollStudent = async (bookId) => {
        const accessToken = localStorage.getItem("accessToken");
        try {
            const response = await fetch(`${server_url}/enroll-student/${bookId}`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            message.success("Enrolled successfully!");
            setBooks((prevBooks) =>
                prevBooks.map((book) =>
                    book.book_id === bookId ? { ...book, is_enrolled: true } : book
                )
            );
        } catch (error) {
            console.error("Error enrolling student:", error);
            message.error("Failed to enroll in the course.");
        }
    };

    const playAudioLecture = async () => {
        // Pause if already playing
        if (isPlaying) {
            if (audioRef.current) {
                audioRef.current.pause();
            }
            setIsPlaying(false);
            return;
        }

        // Resume from paused position if audio already loaded
        if (audioUrl && audioRef.current) {
            audioRef.current.play().catch((error) => {
                console.error("Resume failed:", error);
            });
            setIsPlaying(true);
            return;
        }

        // First time: fetch TTS audio from backend
        const accessToken = localStorage.getItem("accessToken");
        const formData = new FormData();
        formData.append("content_data", topicContent);

        setIsGenerating(true);
        try {
            const response = await fetch(`${server_url}/audio_response`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                body: formData,
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const audioBlob = await response.blob();
            const newAudioUrl = URL.createObjectURL(audioBlob);
            setAudioUrl(newAudioUrl);
            setIsGenerating(false);
            setIsPlaying(true);

            setTimeout(() => {
                if (audioRef.current) {
                    audioRef.current.play().catch((error) => {
                        console.error("Autoplay failed:", error);
                    });
                }
            }, 0);
        } catch (error) {
            console.error("Error fetching audio response:", error);
            setIsGenerating(false);
        }
    };

    const handleBookClick = (book) => {
        if (!book.is_enrolled) {
            message.warning("You must enroll in this course to access its content.");
            return;
        }
        setSelectedBook(book);
        fetchTopics(book.book_id);
    };

    const handleTopicChange = async (newTopic) => {
        if (selectedTopic) {
            await sendScrollPercentage(selectedTopic.topic_id, Math.round(scrollPercentage));
        }
        setSelectedTopic(newTopic);
        fetchTopicContent(newTopic.topic_id);
        setScrollPercentage(0);
    };

    const handleNextTopic = () => {
        const currentIndex = topics.findIndex((t) => t.topic_id === selectedTopic?.topic_id);
        if (currentIndex < topics.length - 1) {
            handleTopicChange(topics[currentIndex + 1]);
        }
    };

    const handlePreviousTopic = () => {
        const currentIndex = topics.findIndex((t) => t.topic_id === selectedTopic?.topic_id);
        if (currentIndex > 0) {
            setScrollPercentage(0);
            handleTopicChange(topics[currentIndex - 1]);

        }
    };

    const handleScroll = () => {
        if (contentRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
            const scrolled = (scrollTop / (scrollHeight - clientHeight)) * 100;
            setScrollPercentage(scrolled);
        }
    };
    const renderTopicButtons = () => (
        <div
            style={{
                marginTop: "20px",
                display: "flex",
                justifyContent: "space-between",
            }}
        >
            <Button
                type="primary"
                icon={<LeftOutlined />}
                onClick={handlePreviousTopic}
                disabled={!selectedTopic || topics.findIndex((t) => t.topic_id === selectedTopic.topic_id) === 0}
            >
                Previous
            </Button>
            <Button
                type="primary"
                icon={<RightOutlined />}
                onClick={handleNextTopic}
                disabled={
                    !selectedTopic ||
                    topics.findIndex((t) => t.topic_id === selectedTopic.topic_id) === topics.length - 1
                }
            >
                Next
            </Button>
        </div>
    );

    const handleMaterialsClick = (materials) => {
        setMaterialsModal({ open: true, materials });
    };

    const closeMaterialsModal = () => {
        setMaterialsModal({ open: false, materials: [] });
    };

    const backToBookSelection = async () => {
        if (selectedTopic) {
            await sendScrollPercentage(selectedTopic.topic_id, Math.round(scrollPercentage));
        }
        setSelectedBook(null);
        setSelectedTopic(null);
        setTopics([]);
        setTopicContent("");
        setAudioUrl(null);
        setIsPlaying(false);
    };

    // For audio control and UI updates
    useEffect(() => {
        if (audioRef.current) {
            const handleAudioEnd = () => {
                setIsPlaying(false);
            };

            audioRef.current.addEventListener('ended', handleAudioEnd);

            return () => {
                if (audioRef.current) {
                    audioRef.current.removeEventListener('ended', handleAudioEnd);
                }
            };
        }
    }, [audioRef.current]);

    return (
        <Layout>
            {/*<div className="container mx-auto py-6">*/}
            {/*    <Title level={2} className="text-center font-gotham mb-6">*/}
            {/*        Courses*/}
            {/*    </Title>*/}
            {/*    {loadingBooks ? (*/}
            {/*        <div className="flex justify-center items-center h-64">*/}
            {/*            <Spin size="large"/>*/}
            {/*        </div>*/}
            {/*    ) : (*/}
            {/*        <Row gutter={[16, 16]}>*/}
            {/*            {books.map((book) => (*/}
            {/*                <Col xs={24} sm={12} md={8} lg={6} key={book.book_id}>*/}
            {/*                    <Card*/}
            {/*                        hoverable*/}
            {/*                        cover={*/}
            {/*                            book.icon_path !== "No icon available" ? (*/}
            {/*                                <img alt={book.name} src={`${server_url}/${book.icon_path}`}*/}
            {/*                                     className="h-40 object-cover"/>*/}
            {/*                            ) : null*/}
            {/*                        }*/}
            {/*                        onClick={() => handleBookClick(book)}*/}
            {/*                    >*/}
            {/*                        <Card.Meta title={book.name} description={book.description}/>*/}
            {/*                        <p className="mt-4">*/}
            {/*                            <strong>Hours to Complete:</strong> {book.hours_to_complete}*/}
            {/*                        </p>*/}
            {/*                        <div className="flex justify-between items-center mt-4">*/}
            {/*                            <Button*/}
            {/*                                type="primary"*/}
            {/*                                icon={<PlusCircleOutlined/>}*/}
            {/*                                disabled={book.is_enrolled}*/}
            {/*                                onClick={(e) => {*/}
            {/*                                    e.stopPropagation();*/}
            {/*                                    enrollStudent(book.book_id);*/}
            {/*                                }}*/}
            {/*                            >*/}
            {/*                                {book.is_enrolled ? "Enrolled" : "Enroll"}*/}
            {/*                            </Button>*/}
            {/*                            {book.material_files.length > 0 && (*/}
            {/*                                <Button*/}
            {/*                                    type="default"*/}
            {/*                                    icon={<FileOutlined/>}*/}
            {/*                                    onClick={(e) => {*/}
            {/*                                        e.stopPropagation();*/}
            {/*                                        handleMaterialsClick(book.material_files);*/}
            {/*                                    }}*/}
            {/*                                >*/}
            {/*                                    Materials*/}
            {/*                                </Button>*/}
            {/*                            )}*/}
            {/*                        </div>*/}
            {/*                    </Card>*/}
            {/*                </Col>*/}
            {/*            ))}*/}
            {/*        </Row>*/}
            {/*    )}*/}

            {/*    {selectedBook && (*/}
            {/*        <div*/}
            {/*            style={{*/}
            {/*                display: "flex",*/}
            {/*                gap: "16px",*/}
            {/*                alignItems: "flex-start",*/}
            {/*                height: "80vh", // Ensure equal height for both sections*/}
            {/*                fontFamily: "Gotham, sans-serif",*/}
            {/*            }}*/}
            {/*        >*/}
            {/*            <Row gutter={[16, 16]} className="mt-8">*/}
            {/*                /!* Topics Section *!/*/}
            {/*                <Col*/}
            {/*                    xs={24}*/}
            {/*                    sm={8}*/}
            {/*                    md={6}*/}
            {/*                    style={{*/}
            {/*                        flex: "1",*/}
            {/*                        borderRadius: "15px",*/}
            {/*                        background: "linear-gradient(to bottom right, #f5faff, #eef7fe)",*/}
            {/*                        padding: "20px",*/}
            {/*                        marginRight: "16px",*/}
            {/*                        boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",*/}
            {/*                        overflowY: "auto",*/}
            {/*                        maxHeight: "80vh",*/}
            {/*                        fontFamily: "Gotham, sans-serif",*/}
            {/*                        marginBottom: "20px",*/}
            {/*                    }}*/}
            {/*                >*/}
            {/*                    <div*/}
            {/*                        style={{*/}
            {/*                            fontWeight: "bold",*/}
            {/*                            fontSize: "18px",*/}
            {/*                            color: "#333",*/}
            {/*                            marginBottom: "16px",*/}
            {/*                            fontFamily: "Gotham, sans-serif",*/}
            {/*                        }}>*/}
            {/*                        Topics*/}
            {/*                    </div>*/}
            {/*                    {topics.map((topic) => (*/}
            {/*                        <div*/}
            {/*                            key={topic.topic_id}*/}
            {/*                            style={{*/}
            {/*                                borderRadius: "10px",*/}
            {/*                                padding: "12px",*/}
            {/*                                marginBottom: "10px",*/}
            {/*                                fontFamily: "Gotham, sans-serif",*/}
            {/*                                backgroundColor:*/}
            {/*                                    selectedTopic && selectedTopic.topic_id === topic.topic_id ? "#d1eaff" : "#f5f5f5",*/}
            {/*                                cursor: "pointer",*/}
            {/*                                transition: "all 0.3s ease",*/}
            {/*                                fontWeight: selectedTopic && selectedTopic.topic_id === topic.topic_id ? "bold" : "normal",*/}
            {/*                            }}*/}
            {/*                            onClick={() => handleTopicChange(topic)}*/}
            {/*                            onMouseEnter={(e) => {*/}
            {/*                                if (!(selectedTopic && selectedTopic.topic_id === topic.topic_id))*/}
            {/*                                    e.target.style.backgroundColor = "#e8f4ff";*/}
            {/*                            }}*/}
            {/*                            onMouseLeave={(e) => {*/}
            {/*                                if (!(selectedTopic && selectedTopic.topic_id === topic.topic_id))*/}
            {/*                                    e.target.style.backgroundColor = "#f5f5f5";*/}
            {/*                            }}*/}
            {/*                        >*/}
            {/*                            {topic.name}*/}
            {/*                        </div>*/}
            {/*                    ))}*/}
            {/*                </Col>*/}

            {/*                /!* Content Section *!/*/}
            {/*                <Col*/}
            {/*                    onScroll={handleScroll}*/}
            {/*                    xs={24}*/}
            {/*                    sm={16}*/}
            {/*                    md={18}*/}
            {/*                    style={{*/}
            {/*                        flex: "3",*/}
            {/*                        borderRadius: "15px",*/}
            {/*                        background: "white",*/}
            {/*                        fontFamily: "Gotham, sans-serif",*/}
            {/*                        padding: "20px",*/}
            {/*                        boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",*/}
            {/*                        overflowY: "auto",*/}
            {/*                        maxHeight: "80vh",*/}
            {/*                        marginBottom: "20px",*/}
            {/*                    }}*/}
            {/*                >*/}
            {/*                    <ReactMarkdown*/}
            {/*                        remarkPlugins={[remarkMath]}*/}
            {/*                        rehypePlugins={[rehypeKatex]}*/}
            {/*                        className="markdown-content"*/}
            {/*                        style={{*/}
            {/*                            fontFamily: "Gotham, sans-serif",*/}
            {/*                            lineHeight: "1.6",*/}
            {/*                        }}*/}
            {/*                    >*/}
            {/*                        {topicContent}*/}
            {/*                    </ReactMarkdown>*/}


            {/*                    /!* Next and Previous Buttons *!/*/}
            {/*                    <div*/}
            {/*                        style={{*/}
            {/*                            marginTop: "20px",*/}
            {/*                            display: "flex",*/}
            {/*                            fontFamily: "Gotham, sans-serif",*/}
            {/*                            justifyContent: "space-between",*/}
            {/*                        }}*/}
            {/*                    >*/}
            {/*                        <Button*/}
            {/*                            type="primary"*/}
            {/*                            icon={<LeftOutlined/>}*/}
            {/*                            onClick={handlePreviousTopic}*/}
            {/*                            disabled={!selectedTopic || topics.findIndex((t) => t.topic_id === selectedTopic.topic_id) === 0}*/}
            {/*                        >*/}
            {/*                            Previous*/}
            {/*                        </Button>*/}
            {/*                        <Button*/}
            {/*                            type="primary"*/}
            {/*                            icon={<RightOutlined/>}*/}
            {/*                            onClick={handleNextTopic}*/}
            {/*                            disabled={*/}
            {/*                                !selectedTopic ||*/}
            {/*                                topics.findIndex((t) => t.topic_id === selectedTopic.topic_id) === topics.length - 1*/}
            {/*                            }*/}
            {/*                        >*/}
            {/*                            Next*/}
            {/*                        </Button>*/}
            {/*                    </div>*/}
            {/*                </Col>*/}
            {/*            </Row>*/}
            {/*        </div>*/}

            {/*    )}*/}

            {/*    <Modal*/}
            {/*        title="Course Materials"*/}
            {/*        open={materialsModal.open}*/}
            {/*        footer={null}*/}
            {/*        onCancel={closeMaterialsModal}*/}
            {/*    >*/}
            {/*        <List*/}
            {/*            dataSource={materialsModal.materials}*/}
            {/*            renderItem={(item) => (*/}
            {/*                <List.Item>*/}
            {/*                    <a href={`${server_url}/${item}`} target="_blank" rel="noopener noreferrer">*/}
            {/*                        {item.split("/").pop()}*/}
            {/*                    </a>*/}
            {/*                </List.Item>*/}
            {/*            )}*/}
            {/*        />*/}
            {/*    </Modal>*/}
            {/*</div>*/}
            <div className="min-h-screen py-6 sm:px-6 lg:px-8 font-[poppins]">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div
                        className=" top-0 z-10 backdrop-blur-sm py-6 mb-6">
                        <div className="flex items-center justify-center">
                            {selectedBook ? (
                                <div className="flex items-center w-full">
                                    <button
                                        onClick={backToBookSelection}
                                        className="mr-4 p-2 rounded-full bg-white shadow-sm hover:bg-blue-50 transition-colors"
                                    >
                                        <ArrowLeft className="h-5 w-5 text-blue-600" />
                                    </button>
                                    <h1 className="text-3xl font-bold text-blue-800 text-center flex-grow">{selectedBook.name}</h1>
                                </div>
                            ) : (
                                <h1 className="text-4xl font-bold text-[#D67C2C] text-center">Courses</h1>
                            )}
                        </div>
                    </div>

                    {/* Course Selection View */}
                    {!selectedBook && (
                        <AnimatePresence>
                            {loadingBooks ? (
                                <div className="flex justify-center items-center h-64">
                                    <div
                                        className="animate-spin h-12 w-12 rounded-full border-4 border-blue-500 border-t-transparent"></div>
                                </div>
                            ) : (
                                <motion.div
                                    variants={containerVariants}
                                    initial="hidden"
                                    animate="show"
                                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8"
                                >
                                    {books.map((book, idx) => (
                                        <motion.div
                                            key={book.book_id}
                                            variants={getCardVariants(idx, books.length)}
                                            whileHover={{
                                                y: -12,
                                                scale: 1.03,
                                                boxShadow: "0 20px 30px -10px rgba(241, 124, 69, 0.3), 4px 8px 0 #F17C45"
                                            }}
                                            transition={{ type: "spring", stiffness: 350, damping: 20 }}
                                            onClick={() => handleBookClick(book)}
                                            className={`bg-white rounded-xl border-2 border-[#D67C2C] shadow-lg hover:shadow-xl overflow-hidden cursor-pointer relative`}
                                        >
                                            {/* Color bar at top */}
                                            <div className="h-3 bg-[#D67C2C]"></div>

                                            {/* Book content */}
                                            <div className="p-6 flex flex-col h-full">
                                                {/* Book icon/image */}
                                                {book.icon_path !== "No icon available" ? (
                                                    <div className="mb-4 h-40 overflow-hidden rounded-lg">
                                                        <img
                                                            src={`${server_url}/${book.icon_path}`}
                                                            alt={book.name}
                                                            className="w-full h-full object-cover transition-transform hover:scale-105"
                                                        />
                                                    </div>
                                                ) : (
                                                    <div
                                                        className="mb-4 h-40 bg-blue-100 rounded-lg flex items-center justify-center">
                                                        <BookMarked className="h-16 w-16 text-[#D67C2C]" />
                                                    </div>
                                                )}

                                                {/* Book title */}
                                                <h3 className="text-xl font-bold text-gray-800 mb-2 line-clamp-2">{book.name}</h3>

                                                {/* Book description */}
                                                <p className="text-gray-600 mb-4 line-clamp-3 text-sm flex-grow">{book.description}</p>

                                                {/* Hours to complete */}
                                                {/*<div className="flex items-center text-gray-700 mb-2">*/}
                                                {/*    <Clock className="h-4 w-4 mr-2 text-[#D67C2C]"/>*/}
                                                {/*    <span className="text-sm font-medium">{book.hours_to_complete} hours to complete</span>*/}
                                                {/*</div>*/}

                                                {/* Competency alignment indicator */}
                                                {book.competency_aligned && (
                                                    <div className="flex items-center text-green-700 mb-2">
                                                        <Award className="h-4 w-4 mr-2 text-green-600" />
                                                        <span className="text-sm font-medium">Curriculum Aligned</span>
                                                    </div>
                                                )}

                                                {book.selected_competencies && book.selected_competencies.length > 0 && (
                                                    <div className="mb-4">
                                                        <div className="flex items-center text-gray-600 mb-1">
                                                            <Target className="h-3 w-3 mr-1" />
                                                            <span className="text-xs font-medium">
                                                                {book.selected_competencies.length} competencies covered
                                                            </span>
                                                        </div>
                                                        <div className="flex flex-wrap gap-1">
                                                            {book.selected_competencies.slice(0, 3).map((comp, index) => (
                                                                <span
                                                                    key={index}
                                                                    className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full"
                                                                >
                                                                    {comp.competency_id || comp}
                                                                </span>
                                                            ))}
                                                            {book.selected_competencies.length > 3 && (
                                                                <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                                                                    +{book.selected_competencies.length - 3} more
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Action buttons */}
                                                <div className="flex gap-2 mt-auto">
                                                    <button
                                                        onClick={(e) => enrollStudent(book.book_id, e)}
                                                        disabled={book.is_enrolled}
                                                        className={`flex-1 py-2.5 px-4 rounded-lg flex items-center justify-center text-sm font-semibold ${book.is_enrolled
                                                            ? "bg-gray-100 text-gray-500 border border-gray-200"
                                                            : "bg-[#D67C2C] text-white hover:bg-orange-400 shadow-md hover:shadow-lg transition-all"
                                                            }`}
                                                    >
                                                        {book.is_enrolled ? (
                                                            <>
                                                                <CheckCircle className="h-4 w-4 mr-2" />
                                                                Enrolled
                                                            </>
                                                        ) : (
                                                            <>
                                                                <PlusCircle className="h-4 w-4 mr-2" />
                                                                Enroll
                                                            </>
                                                        )}
                                                    </button>

                                                    {book.material_files.length > 0 && (
                                                        <button
                                                            onClick={(e) => handleMaterialsClick(book.material_files, e)}
                                                            className="py-2.5 px-4 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-gray-700 flex items-center justify-center text-sm font-medium"
                                                        >
                                                            <FileText className="h-4 w-4 mr-2 text-[#D67C2C]" />
                                                            Materials
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </motion.div>
                                    ))}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    )}

                    {/* Course Content View */}
                    {selectedBook && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="grid grid-cols-1 lg:grid-cols-4 gap-6"
                        >
                            {/* Topics Sidebar */}
                            <div className="lg:col-span-1">
                                <div className="bg-white rounded-xl shadow-lg overflow-hidden h-full">
                                    <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-4">
                                        <h3 className="text-white font-bold text-lg">Topics</h3>
                                    </div>
                                    {loadingTopics ? (
                                        <div className="flex justify-center items-center h-32">
                                            <div
                                                className="animate-spin h-8 w-8 rounded-full border-4 border-blue-500 border-t-transparent"></div>
                                        </div>
                                    ) : (
                                        <div className="p-4 space-y-2 max-h-[70vh] overflow-y-auto">
                                            {topics.map((topic) => (
                                                <motion.button
                                                    key={topic.topic_id}
                                                    onClick={() => handleTopicChange(topic)}
                                                    whileHover={{ scale: 1.02 }}
                                                    whileTap={{ scale: 0.98 }}
                                                    className={`w-full p-3 text-left rounded-lg transition-all ${selectedTopic && selectedTopic.topic_id === topic.topic_id
                                                        ? "bg-blue-100 text-blue-800 font-medium"
                                                        : "bg-gray-50 text-gray-700 hover:bg-blue-50"
                                                        }`}
                                                >
                                                    <div className="flex items-center">
                                                        <BookOpen className={`h-4 w-4 mr-2 ${selectedTopic && selectedTopic.topic_id === topic.topic_id
                                                            ? "text-blue-600"
                                                            : "text-gray-500"
                                                            }`} />
                                                        <span className="line-clamp-2">{topic.name}</span>
                                                    </div>
                                                </motion.button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Content Area */}
                            <div
                                className="lg:col-span-3 bg-white rounded-xl shadow-lg overflow-hidden flex flex-col h-full"
                                style={{ minHeight: "calc(100vh - 180px)" }}
                            >
                                {/* Audio Controls */}
                                <div
                                    className="bg-gradient-to-r from-gray-50 to-blue-50 p-4 border-b border-gray-200 flex justify-between items-center">
                                    <div className="flex items-center">
                                        {selectedTopic && (
                                            <div className="text-blue-800 font-medium truncate max-w-md">
                                                {selectedTopic.name}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex space-x-2">
                                        <motion.button
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            onClick={playAudioLecture}
                                            disabled={isGenerating}
                                            className={`flex items-center px-4 py-2 rounded-lg ${isGenerating
                                                ? "bg-gray-100 text-gray-400"
                                                : "bg-blue-600 text-white hover:bg-blue-700"
                                                } transition-colors`}
                                        >
                                            {isGenerating ? (
                                                <>
                                                    <Loader className="h-5 w-5 mr-2 animate-spin" />
                                                    Generating...
                                                </>
                                            ) : isPlaying ? (
                                                <>
                                                    <Pause className="h-5 w-5 mr-2" />
                                                    Pause Audio
                                                </>
                                            ) : (
                                                <>
                                                    <Volume2 className="h-5 w-5 mr-2" />
                                                    {audioUrl ? "Resume Audio" : "Listen to Lecture"}
                                                </>
                                            )}
                                        </motion.button>
                                    </div>
                                </div>

                                {/* Content */}
                                <div
                                    ref={contentRef}
                                    onScroll={handleScroll}
                                    className="flex-grow p-6 overflow-y-auto"
                                >
                                    <ReactMarkdown
                                        remarkPlugins={[remarkMath]}
                                        rehypePlugins={[rehypeKatex]}
                                        className="prose prose-blue max-w-none"
                                    >
                                        {topicContent}
                                    </ReactMarkdown>
                                </div>

                                {/* Navigation Footer */}
                                <div
                                    className="border-t border-gray-200 p-4 flex justify-between items-center bg-gray-50">
                                    <motion.button
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={handlePreviousTopic}
                                        disabled={!selectedTopic || topics.findIndex((t) => t.topic_id === selectedTopic.topic_id) === 0}
                                        className={`flex items-center px-4 py-2 rounded-lg ${!selectedTopic || topics.findIndex((t) => t.topic_id === selectedTopic.topic_id) === 0
                                            ? "bg-gray-100 text-gray-400"
                                            : "bg-white border border-blue-200 text-blue-700 hover:bg-blue-50"
                                            } transition-colors`}
                                    >
                                        <ChevronLeft className="h-5 w-5 mr-2" />
                                        Previous Topic
                                    </motion.button>

                                    <div className="text-sm text-gray-500">
                                        {selectedTopic && topics.length > 0 && (
                                            <span>
                                                {topics.findIndex((t) => t.topic_id === selectedTopic.topic_id) + 1} of {topics.length}
                                            </span>
                                        )}
                                    </div>

                                    <motion.button
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={handleNextTopic}
                                        disabled={
                                            !selectedTopic ||
                                            topics.findIndex((t) => t.topic_id === selectedTopic.topic_id) === topics.length - 1
                                        }
                                        className={`flex items-center px-4 py-2 rounded-lg ${!selectedTopic ||
                                            topics.findIndex((t) => t.topic_id === selectedTopic.topic_id) === topics.length - 1
                                            ? "bg-gray-100 text-gray-400"
                                            : "bg-white border border-blue-200 text-blue-700 hover:bg-blue-50"
                                            } transition-colors`}
                                    >
                                        Next Topic
                                        <ChevronRight className="h-5 w-5 ml-2" />
                                    </motion.button>
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {/* Materials Modal */}
                    {materialsModal.open && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.9 }}
                                className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
                            >
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-xl font-bold text-gray-800">Course Materials</h3>
                                    <button
                                        onClick={closeMaterialsModal}
                                        className="p-1 rounded-full hover:bg-gray-100 transition-colors"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-500"
                                            fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>

                                <div className="space-y-2">
                                    {materialsModal.materials.map((item, index) => (
                                        <a
                                            key={index}
                                            href={`${server_url}/${item}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center p-3 rounded-lg bg-gray-50 hover:bg-blue-50 transition-colors group"
                                        >
                                            <FileText className="h-5 w-5 text-[#D67C2C] mr-3" />
                                            <span
                                                className="text-gray-700 group-hover:text-blue-700">{item.split("/").pop()}</span>
                                            <ExternalLink
                                                className="h-4 w-4 text-gray-400 ml-auto group-hover:text-[#D67C2C]" />
                                        </a>
                                    ))}
                                </div>
                            </motion.div>
                        </div>
                    )}

                    {/* Hidden audio element */}
                    <audio ref={audioRef} src={audioUrl} className="hidden" />
                </div>
            </div>
        </Layout>
    );
};

export default CoursesPage;
